import { LoginIdPasswordParametersModelDto } from '@treasury/api/bo';
import { DomainEntity } from '../../../../shared';

export class LoginParameters extends DomainEntity<LoginIdPasswordParametersModelDto> {
    public static from(dto: LoginIdPasswordParametersModelDto): LoginParameters {
        return new LoginParameters(dto);
    }

    public createDefault(): this {
        throw new Error('createDefault not implemented');
    }

    public get id() {
        return this.dto.id;
    }

    public get isDefault() {
        return this.dto.isDefault;
    }

    // User ID settings
    public get loginIdMinLength() {
        return this.dto.loginIdMinLength;
    }

    public set loginIdMinLength(value: number) {
        this.dto.loginIdMinLength = value;
    }

    public get loginIdMaxLength() {
        return this.dto.loginIdMaxLength;
    }

    public set loginIdMaxLength(value: number) {
        this.dto.loginIdMaxLength = value;
    }

    public get allowAlphabetsInLoginId() {
        return this.dto.allowAlphabetsInLoginId;
    }

    public set allowAlphabetsInLoginId(value: boolean) {
        this.dto.allowAlphabetsInLoginId = value;
        if (!value) {
            this.dto.alphabetsInLoginIdAreRequired = false;
        }
    }

    public get allowNumbersInLoginId() {
        return this.dto.allowNumbersInLoginId;
    }

    public set allowNumbersInLoginId(value: boolean) {
        this.dto.allowNumbersInLoginId = value;
        if (!value) {
            this.dto.numbersInLoginIdAreRequired = false;
        }
    }

    public get allowSpecialCharactersInLoginId() {
        return this.dto.allowSpecialCharactersInLoginId;
    }

    public set allowSpecialCharactersInLoginId(value: boolean) {
        this.dto.allowSpecialCharactersInLoginId = value;
        if (!value) {
            this.dto.specialCharactersInLoginIdAreRequired = false;
        }
    }

    public get alphabetsInLoginIdAreRequired() {
        return this.dto.alphabetsInLoginIdAreRequired;
    }

    public set alphabetsInLoginIdAreRequired(value: boolean) {
        this.dto.alphabetsInLoginIdAreRequired = value;
    }

    public get numbersInLoginIdAreRequired() {
        return this.dto.numbersInLoginIdAreRequired;
    }

    public set numbersInLoginIdAreRequired(value: boolean) {
        this.dto.numbersInLoginIdAreRequired = value;
    }

    // Password settings
    public get passwordMinLength() {
        return this.dto.passwordMinLength;
    }

    public set passwordMinLength(value: number) {
        this.dto.passwordMinLength = value;
    }

    public get passwordMaxLength() {
        return this.dto.passwordMaxLength;
    }

    public set passwordMaxLength(value: number) {
        this.dto.passwordMaxLength = value;
    }

    public get allowAlphabetsInPassword() {
        return this.dto.allowAlphabetsInPassword;
    }

    public set allowAlphabetsInPassword(value: boolean) {
        this.dto.allowAlphabetsInPassword = value;
        if (!value) {
            this.dto.alphabetsInPasswordAreRequired = false;
        }
    }

    public get allowNumbersInPassword() {
        return this.dto.allowNumbersInPassword;
    }

    public set allowNumbersInPassword(value: boolean) {
        this.dto.allowNumbersInPassword = value;
        if (!value) {
            this.dto.numbersInPasswordAreRequired = false;
        }
    }

    public get allowSpecialCharactersInPassword() {
        return this.dto.allowSpecialCharactersInPassword;
    }

    public set allowSpecialCharactersInPassword(value: boolean) {
        this.dto.allowSpecialCharactersInPassword = value;
        if (!value) {
            this.dto.specialCharactersInPasswordAreRequired = false;
        }
    }

    public get alphabetsInPasswordAreRequired() {
        return this.dto.alphabetsInPasswordAreRequired;
    }

    public set alphabetsInPasswordAreRequired(value: boolean) {
        this.dto.alphabetsInPasswordAreRequired = value;
    }

    public get numbersInPasswordAreRequired() {
        return this.dto.numbersInPasswordAreRequired;
    }

    public set numbersInPasswordAreRequired(value: boolean) {
        this.dto.numbersInPasswordAreRequired = value;
    }

    public get specialCharactersInPasswordAreRequired() {
        return this.dto.specialCharactersInPasswordAreRequired;
    }

    public set specialCharactersInPasswordAreRequired(value: boolean) {
        this.dto.specialCharactersInPasswordAreRequired = value;
    }

    public get numberOfUppercaseRequiredInPassword() {
        return this.dto.numberOfUppercaseRequiredInPassword ?? 0;
    }

    public set numberOfUppercaseRequiredInPassword(value: number) {
        this.dto.numberOfUppercaseRequiredInPassword = value;
    }

    public get numberOfLowercaseRequiredInPassword() {
        return this.dto.numberOfLowercaseRequiredInPassword ?? 0;
    }

    public set numberOfLowercaseRequiredInPassword(value: number) {
        this.dto.numberOfLowercaseRequiredInPassword = value;
    }

    public get willPasswordExpire() {
        return this.dto.willPasswordExpire;
    }

    public set willPasswordExpire(value: boolean) {
        this.dto.willPasswordExpire = value;
    }

    public get passwordExpireInDays() {
        return this.dto.passwordExpireInDays ?? 30;
    }

    public set passwordExpireInDays(value: number) {
        this.dto.passwordExpireInDays = value;
    }

    public get warnPasswordExpireInDays() {
        return this.dto.warnPasswordExpireInDays;
    }

    public set warnPasswordExpireInDays(value: number) {
        this.dto.warnPasswordExpireInDays = value;
    }

    public get numberOfPrevUsedPasswords() {
        return this.dto.numberOfPrevUsedPasswords ?? 0;
    }

    public set numberOfPrevUsedPasswords(value: number) {
        this.dto.numberOfPrevUsedPasswords = value;
    }

    public get numberOfAllowedAttemptsBeforeLock() {
        return this.dto.numberOfAllowedAttemptsBeforeLock ?? 3;
    }

    public set numberOfAllowedAttemptsBeforeLock(value: number) {
        this.dto.numberOfAllowedAttemptsBeforeLock = value;
    }

    public get invalidLoginIdPasswordMessage() {
        return this.dto.invalidLoginIdPasswordMessage;
    }

    public set invalidLoginIdPasswordMessage(value: string) {
        this.dto.invalidLoginIdPasswordMessage = value;
    }

    public get allowLockedOutMessage() {
        return this.dto.allowLockedOutMessage;
    }

    public set allowLockedOutMessage(value: boolean) {
        this.dto.allowLockedOutMessage = value;
    }

    public get lockedOutMessage() {
        return this.dto.lockedOutMessage;
    }

    public set lockedOutMessage(value: string) {
        this.dto.lockedOutMessage = value;
    }

    public get sendLockedEmail() {
        return this.dto.sendLockedEmail;
    }

    public set sendLockedEmail(value: boolean) {
        this.dto.sendLockedEmail = value;
    }

    public get lockedEmailMessage() {
        return this.dto.lockedEmailMessage;
    }

    public set lockedEmailMessage(value: string) {
        this.dto.lockedEmailMessage = value;
    }

    public get sessionTimeoutMinutes() {
        return this.dto.sessionTimeoutMinutes;
    }

    public set sessionTimeoutMinutes(value: number) {
        this.dto.sessionTimeoutMinutes = value;
    }

    public get redirectUrl() {
        return this.dto.redirectUrl ?? '';
    }

    public set redirectUrl(value: string) {
        this.dto.redirectUrl = value;
    }

    public get isLoginIdMinLengthValid() {
        return this.dto.loginIdMinLength <= this.dto.loginIdMaxLength;
    }

    public get isLoginIdCharTypeValid() {
        return (
            this.dto.allowAlphabetsInLoginId ||
            this.dto.allowNumbersInLoginId ||
            this.dto.allowSpecialCharactersInLoginId
        );
    }

    public get isPasswordMinLengthValid() {
        return this.dto.passwordMinLength <= this.dto.passwordMaxLength;
    }

    public get isPasswordMaxLengthValid() {
        return this.dto.passwordMaxLength <= 256;
    }

    public get isPasswordCharTypeValid() {
        return (
            this.dto.allowAlphabetsInPassword ||
            this.dto.allowNumbersInPassword ||
            this.dto.allowSpecialCharactersInPassword
        );
    }

    public get isAlphabetRequiredValid() {
        return !(
            this.dto.alphabetsInPasswordAreRequired &&
            this.dto.numberOfUppercaseRequiredInPassword === 0 &&
            this.dto.numberOfLowercaseRequiredInPassword === 0
        );
    }

    public get isValid() {
        return (
            this.isLoginIdMinLengthValid &&
            this.isLoginIdCharTypeValid &&
            this.isPasswordMinLengthValid &&
            this.isPasswordMaxLengthValid &&
            this.isPasswordCharTypeValid &&
            this.isAlphabetRequiredValid &&
            this.dto.redirectUrl !== undefined &&
            this.dto.redirectUrl.trim().length > 0
        );
    }
}
